import { noteService } from "../services/noteService";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/bmp",
];

export interface IValidationResult {
  valid: boolean;
  error?: string;
}

export interface IExtractedImage {
  base64Data: string;
  mimeType: string;
  placeholder: string;
  fullMatch: string;
}

/**
 * Validate image file before upload
 */
export const validateNoteImage = (file: File): IValidationResult => {
  if (!file) {
    return { valid: false, error: "No file provided" };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: "Image must be under 5MB" };
  }

  if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      error: "Only PNG, JPEG, GIF, WebP, and BMP images allowed",
    };
  }

  return { valid: true };
};

/**
 * Replace image keys with pre-signed URLs for rendering
 * @param htmlContent - HTML content with note-image: references
 * @param noteId - Note ID to fetch images for
 * @returns HTML with pre-signed URLs
 */
export const renderNoteWithImages = async (
  htmlContent: string,
  noteId: number
): Promise<string> => {
  if (!htmlContent) return htmlContent;

  // Check if there are any note-image references
  const noteImagePattern = /src="note-image:([^"]+)"/g;
  const hasNoteImages = noteImagePattern.test(htmlContent);

  if (!hasNoteImages) {
    // No note-image references, return as-is (might have base64 images)
    return htmlContent;
  }

  try {
    // Fetch pre-signed URLs for this note
    const images = await noteService.getNoteImages(noteId);

    // Create a map of keys to URLs
    const imageMap = new Map(images.map((img) => [img.key, img.preSignedUrl]));

    // Replace all note-image: references with pre-signed URLs
    let transformedHtml = htmlContent;
    const matches = Array.from(
      htmlContent.matchAll(/src="note-image:([^"]+)"/g)
    );

    matches.forEach((match) => {
      const key = match[1];
      const preSignedUrl = imageMap.get(key);

      if (preSignedUrl) {
        transformedHtml = transformedHtml.replace(
          `note-image:${key}`,
          preSignedUrl
        );
      }
    });

    return transformedHtml;
  } catch {
    // Return original content on error
    return htmlContent;
  }
};

/**
 * Extract image keys from HTML content
 */
export const extractImageKeys = (htmlContent: string): string[] => {
  if (!htmlContent) return [];

  const matches = Array.from(htmlContent.matchAll(/src="note-image:([^"]+)"/g));
  const keys = matches.map((match) => match[1]);

  return keys;
};

/**
 * Extract base64 images from HTML content
 * @param htmlContent - HTML containing base64 encoded images
 * @returns Array of extracted images with placeholders
 */
export const extractBase64ImagesFromHTML = (
  htmlContent: string
): IExtractedImage[] => {
  if (!htmlContent) return [];

  const base64ImageRegex =
    /<img[^>]+src="data:image\/([^;]+);base64,([^"]+)"[^>]*>/g;
  const matches = Array.from(htmlContent.matchAll(base64ImageRegex));

  return matches.map((match, index) => ({
    fullMatch: match[0],
    mimeType: `image/${match[1]}`,
    base64Data: match[2],
    placeholder: `{{IMAGE_${index}}}`,
  }));
};

/**
 * Convert base64 string to File object
 * @param base64Data - Base64 encoded image data (without data URI prefix)
 * @param mimeType - MIME type of the image
 * @param filename - Filename for the File object
 * @returns File object ready for upload
 */
export const base64ToFile = (
  base64Data: string,
  mimeType: string,
  filename: string
): File => {
  // Convert base64 to binary
  const byteString = atob(base64Data);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i += 1) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  // Create Blob and then File
  const blob = new Blob([uint8Array], { type: mimeType });
  return new File([blob], filename, { type: mimeType });
};

// ===== NEW IN-MEMORY IMAGE HANDLING FUNCTIONS =====

/**
 * Create a temporary placeholder for an image that will be uploaded
 * @param index - Index of the image in the images array
 * @returns Temporary placeholder string (e.g., "note-image:0")
 */
export const createImagePlaceholder = (index: number): string => {
  return `note-image:${index}`;
};

/**
 * Create a blob URL for temporary image preview
 * @param file - File object
 * @returns Blob URL for preview
 */
export const createBlobUrl = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Revoke blob URLs to free up memory
 * @param urls - Array of blob URLs to revoke
 */
export const revokeBlobUrls = (urls: string[]): void => {
  urls.forEach((url) => {
    if (url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  });
};

/**
 * Filter images to only include those actually present in the content
 * This removes images that were uploaded but then deleted from the editor
 * @param htmlContent - HTML content with blob URLs
 * @param blobUrls - Array of all blob URLs created
 * @param images - Array of all image files
 * @param startIndex - Index to start numbering blob URL placeholders (to continue after base64 images)
 * @returns Filtered and reindexed blob URLs, images, and updated HTML
 */
export const filterAndReindexImages = (
  htmlContent: string,
  blobUrls: string[],
  images: File[],
  startIndex = 0
): {
  filteredBlobUrls: string[];
  filteredImages: File[];
  updatedHtml: string;
} => {
  if (!htmlContent) {
    return {
      filteredBlobUrls: [],
      filteredImages: [],
      updatedHtml: htmlContent,
    };
  }

  // Special case: If no blob URLs but we have images and HTML has note-image: placeholders
  // This happens when base64 images were converted by extractAndReplaceBase64Images
  if (blobUrls.length === 0 && images.length > 0) {
    // Check if HTML has note-image: placeholders
    const hasPlaceholders = /note-image:\d+/.test(htmlContent);
    if (hasPlaceholders) {
      // Return all images - they're already indexed as note-image:0, note-image:1, etc.
      return {
        filteredBlobUrls: [],
        filteredImages: images,
        updatedHtml: htmlContent,
      };
    }
    // No placeholders and no blob URLs - no images to send
    return {
      filteredBlobUrls: [],
      filteredImages: [],
      updatedHtml: htmlContent,
    };
  }

  // No blob URLs tracked - return empty
  if (blobUrls.length === 0) {
    return {
      filteredBlobUrls: [],
      filteredImages: [],
      updatedHtml: htmlContent,
    };
  }

  // Find which blob URLs are actually in the content
  const usedBlobUrls: string[] = [];
  const usedImages: File[] = [];

  blobUrls.forEach((blobUrl, index) => {
    if (htmlContent.includes(blobUrl)) {
      usedBlobUrls.push(blobUrl);
      if (images[index]) {
        usedImages.push(images[index]);
      }
    }
  });

  // Replace used blob URLs with sequential placeholders starting from startIndex
  // This ensures blob URLs are indexed after any base64 images
  let updatedHtml = htmlContent;
  usedBlobUrls.forEach((blobUrl, index) => {
    const placeholder = createImagePlaceholder(startIndex + index);

    // Replace blob URLs in img src attributes
    updatedHtml = updatedHtml.replace(new RegExp(blobUrl, "g"), placeholder);

    // Also replace blob URLs in image reference span data-image-key attributes
    // This handles the case where references use blob URLs as temporary IDs
    const escapedBlobUrl = blobUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const referenceRegex = new RegExp(
      `(<span[^>]*class="image-ref"[^>]*data-image-key=")${escapedBlobUrl}("[^>]*>)`,
      "g"
    );
    updatedHtml = updatedHtml.replace(referenceRegex, `$1${placeholder}$2`);
  });

  return {
    filteredBlobUrls: usedBlobUrls,
    filteredImages: usedImages,
    updatedHtml,
  };
};

/**
 * Store image file and return blob URL for preview
 * This is used when users insert images in the editor
 * @param file - Image file
 * @param imageStore - Array to store the file
 * @returns Blob URL for immediate preview
 */
export const storeImageAndGetPreview = (
  file: File,
  imageStore: File[]
): string => {
  // Validate the file first
  const validation = validateNoteImage(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Store the file
  imageStore.push(file);

  // Create and return blob URL for preview
  return createBlobUrl(file);
};

/**
 * Extract base64 images from HTML, convert to File objects, and replace with note-image placeholders
 * This is a safeguard for cases where base64 images might slip through
 * @param htmlContent - HTML content that might contain base64 images
 * @param imageStore - Array to store extracted File objects
 * @param startIndex - Starting index for placeholders (to avoid conflicts with existing images)
 * @returns Object with cleaned HTML and count of extracted images
 */
export const extractAndReplaceBase64Images = (
  htmlContent: string,
  imageStore: File[],
  startIndex = 0
): { cleanedHtml: string; extractedCount: number } => {
  if (!htmlContent) {
    return { cleanedHtml: htmlContent, extractedCount: 0 };
  }

  const extractedImages = extractBase64ImagesFromHTML(htmlContent);

  if (extractedImages.length === 0) {
    return { cleanedHtml: htmlContent, extractedCount: 0 };
  }

  let cleanedHtml = htmlContent;

  // Convert each base64 image to a File and replace with placeholder
  extractedImages.forEach((img, index) => {
    const filename = `note-image-${Date.now()}-${startIndex + index}.${
      img.mimeType.split("/")[1]
    }`;
    const file = base64ToFile(img.base64Data, img.mimeType, filename);

    // Store the file
    imageStore.push(file);

    // Replace base64 image with note-image placeholder
    const placeholder = createImagePlaceholder(startIndex + index);
    cleanedHtml = cleanedHtml.replace(
      img.fullMatch,
      `<img src="${placeholder}">`
    );
  });

  return { cleanedHtml, extractedCount: extractedImages.length };
};
